import { createAsyncThunk } from "@reduxjs/toolkit"
import { setUser, setLoading, setError, setInitialized } from "./authSlice"
import { auth } from "@/lib/firebase"
import { signInWithCustomToken } from "firebase/auth"
import { setHttpAuth } from "@/lib/http"

export const verifySession = createAsyncThunk(
  "auth/verifySession",
  async (_, { dispatch }) => {
    dispatch(setLoading(true))

    try {
      const user = auth.currentUser

      if (!user) {
        dispatch(setUser({ user: null }))
        dispatch(setInitialized(true))
        return null
      }

      const idToken = await user.getIdToken()
      const response = await fetch("/api/auth/verifySession", {
        headers: { Authorization: `Bearer ${idToken}` },
        credentials: "include",
      })

      if (response.ok) {
        const data = await response.json()
        if (data.customToken) {
          await signInWithCustomToken(auth, data.customToken)
          setHttpAuth({ token: idToken })
          dispatch(setUser({
            user: {
              roles: data.roles || [],
              uid: user.uid,
              email: user.email!,
              name: user.displayName || "",
              picture: user.photoURL || "",
              admin: data.admin || false,
              superAdmin: data.superAdmin || false
            }, token: idToken, roles: data.roles || []
          }))
        } else {
          dispatch(setUser({ user: null }))
        }
      } else {
        dispatch(setUser({ user: null }))
      }
    } catch (error) {
      dispatch(setError((error as Error).message))
      dispatch(setUser({ user: null }))
    } finally {
      dispatch(setLoading(false))
      dispatch(setInitialized(true))
    }
  }
)

export const grantAdminAccess = createAsyncThunk(
  "auth/grantAdminAccess",
  async (payload: { email: string; isAdmin?: boolean; isSuperAdmin?: boolean; role?: string }, { dispatch }) => {
    dispatch(setLoading(true))
    try {
      dispatch(setError(null))
      const response = await fetch("/api/auth/setAdminRole", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      })
      if (!response.ok) {
        throw new Error("Failed to grant admin access. Please try again.")
      }
      const res = await response.json()
      if (!res.success) {
        throw new Error(res.error || "Failed to grant admin access")
      }
      return true
    } catch (error) {
      dispatch(setError((error as Error).message))
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  }
)

export const logoutSession = createAsyncThunk(
  "logoutSession",
  async (_, { dispatch }) => {
    try {
      await fetch("/api/auth/sessionLogout", { method: "POST", credentials: "include" })
      await auth.signOut()
      setHttpAuth({ logout: true })
      dispatch(setUser({ user: null }))
    } catch (error) {
      dispatch(setError((error as Error).message))
    }
  }
)
